import { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../lib/supabase.js'

/**
 * Modal that pops on clicking either a leaderboard row OR a points total.
 * Two tabs:
 *   - Posts: list of original posts + reposts with impressions and a link out.
 *   - Points: line-by-line breakdown of every point_event for this participant.
 *
 * `initialTab` lets us land on the right tab depending on what was clicked.
 *
 * Deliberately read-only and minimal — we just want to satisfy the curiosity
 * of "what's Daisy posting that's getting her to 240 pts" without building a
 * second profile page.
 */
export default function ParticipantDetailModal({ participant, initialTab = 'posts', onClose }) {
  const [tab, setTab] = useState(initialTab)
  const [activities, setActivities] = useState([])
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { setTab(initialTab) }, [initialTab])

  useEffect(() => {
    if (!participant?.id || !supabase) return
    let cancelled = false
    setLoading(true)
    Promise.all([
      supabase
        .from('activities')
        .select('id, type, url, created_at, content_gate_passed, post_stats(impressions, fetched_at)')
        .eq('participant_id', participant.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('point_events')
        .select('id, points, reason, created_at, activity_id')
        .eq('participant_id', participant.id)
        .order('created_at', { ascending: false }),
    ]).then(([a, e]) => {
      if (cancelled) return
      setActivities(a.data || [])
      setEvents(e.data || [])
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [participant?.id])

  const totalPoints = useMemo(() => events.reduce((s, e) => s + e.points, 0), [events])
  const visiblePosts = useMemo(
    () => activities.filter(a => a.type === 'post' || a.type === 'repost'),
    [activities],
  )

  if (!participant) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          onClick={e => e.stopPropagation()}
          className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-brand-border flex items-center gap-4">
            <Avatar participant={participant} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-brand-text truncate">{participant.name}</div>
              <div className="text-xs text-brand-text-muted">
                {totalPoints} pts · {visiblePosts.length} post{visiblePosts.length === 1 ? '' : 's'}
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded-md w-8 h-8 flex items-center justify-center text-brand-text-muted hover:text-brand-text hover:bg-brand-bg-muted transition-colors"
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div className="px-6 pt-3 border-b border-brand-border flex gap-1">
            <TabButton active={tab === 'posts'} onClick={() => setTab('posts')}>
              Posts
            </TabButton>
            <TabButton active={tab === 'points'} onClick={() => setTab('points')}>
              Points breakdown
            </TabButton>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading && <div className="text-sm text-brand-text-muted py-8 text-center">Loading…</div>}

            {!loading && tab === 'posts' && (
              <PostsList posts={visiblePosts} />
            )}

            {!loading && tab === 'points' && (
              <PointsBreakdown events={events} activities={activities} />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-semibold rounded-t-md border-b-2 transition-colors ${
        active
          ? 'border-brand-blue text-brand-blue'
          : 'border-transparent text-brand-text-muted hover:text-brand-text'
      }`}
    >
      {children}
    </button>
  )
}

function PostsList({ posts }) {
  if (posts.length === 0) {
    return <p className="text-sm text-brand-text-muted py-8 text-center">No posts logged yet.</p>
  }
  return (
    <ul className="space-y-2">
      {posts.map(p => {
        const impressions = (p.post_stats || []).reduce((m, s) => Math.max(m, s.impressions || 0), 0)
        return (
          <li key={p.id} className="border border-brand-border rounded-lg p-3 hover:border-brand-blue transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-xs text-brand-text-muted uppercase tracking-wider mb-1">
                  {p.type} · {formatDate(p.created_at)}
                </div>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-brand-blue hover:underline break-all"
                >
                  {shortenUrl(p.url)}
                </a>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold text-brand-text">
                  {impressions.toLocaleString()}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">
                  impressions
                </div>
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function PointsBreakdown({ events, activities }) {
  if (events.length === 0) {
    return <p className="text-sm text-brand-text-muted py-8 text-center">No points logged yet.</p>
  }
  const activityById = new Map(activities.map(a => [a.id, a]))
  return (
    <ul className="space-y-1.5">
      {events.map(e => {
        const linked = e.activity_id ? activityById.get(e.activity_id) : null
        return (
          <li key={e.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-brand-bg-muted">
            <div className="min-w-0 flex-1">
              <div className="text-sm text-brand-text">{e.reason}</div>
              <div className="text-xs text-brand-text-muted">
                {formatDate(e.created_at)}
                {linked?.url && (
                  <>
                    {' · '}
                    <a
                      href={linked.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-blue hover:underline"
                    >
                      view post ↗
                    </a>
                  </>
                )}
              </div>
            </div>
            <div className="text-sm font-bold text-brand-blue shrink-0">
              +{e.points}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function Avatar({ participant }) {
  if (participant.avatar_url) {
    return <img src={participant.avatar_url} alt={participant.name} className="w-12 h-12 rounded-full object-cover border-2 border-brand-border" />
  }
  const initials = participant.name?.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?'
  return (
    <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold bg-brand-blue text-white">
      {initials}
    </div>
  )
}

function shortenUrl(url) {
  if (!url) return ''
  try {
    const u = new URL(url)
    const path = u.pathname.length > 40 ? u.pathname.slice(0, 40) + '…' : u.pathname
    return u.host + path
  } catch {
    return url
  }
}

function formatDate(iso) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}
